import type { LinkedAccount } from "@/components/ProfileDrawer/ConnectionsOverlay";
import {
  IMMUTABLE_GAME_NAME,
  SOCIAL_LINK_ERROR_MESSAGES,
  appConfig,
} from "@/constants";
import { useAccessTokenState } from "@/hooks";
import { notifyError } from "@/utils/monitoring";
import { authorizedGet, authorizedPost } from "@/utils/request";
import { trackLegacyEvent } from "@analytics";
import type { Content } from "@prismicio/client";
import type { QuestState } from "@ui-kit/QuestList";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWRImmutable from "swr/immutable";
import { TelegramQuestModalContent } from "./TelegramQuestModalContent";
const POPUP_CHECK_INTERVAL_MS = 500;
const AUTH_GRACE_PERIOD_MS = 1000;
const TELEGRAM_MESSAGE_EVENT_ORIGIN = "https://oauth.telegram.org";
const TELEGRAM_POPUP_NAME = "telegramLogin";
const GENERIC_ERROR_MESSAGE_DETAIL =
  "Please try again later. If the problem persists, please contact support.";

interface ApiError {
  response?: {
    data?: {
      code?: string;
      message?: string;
    };
  };
}

type TelegramQuestModalProps = {
  game?: Content.GameDocument;
  quest: QuestState;
  onClose: () => void;
  onQuestComplete: () => void;
};

type ErrorState = {
  message: string;
  details?: string;
};

type ModalStatus =
  | { status: "idle" }
  | { status: "linkPassport" }
  | { status: "loggingIn" }
  | { status: "loginProcessing" }
  | { status: "loginError"; error: ErrorState }
  | { status: "popupClosed" }
  | { status: "promptJoinChat"; gameName: string }
  | { status: "notJoinedChat"; gameName: string }
  | { status: "promptClaimBotQuest"; username: string | undefined }
  | { status: "completingQuest" }
  | { status: "completingQuestError"; error: ErrorState }
  | { status: "loginErrorSocialAccountAlreadyLinked" };

export const TelegramQuestModal = ({
  game,
  quest,
  onClose,
  onQuestComplete,
}: TelegramQuestModalProps) => {
  const accessToken = useAccessTokenState();
  const linkedAccountsKey = "/passport-profile/v1/linked-accounts";
  const {
    data = { linked_accounts: [] },
    mutate: mutateLinkedAccounts,
    isValidating: isValidatingLinkedAccounts,
  } = useSWRImmutable<{ linked_accounts: LinkedAccount[] }>(
    accessToken ? linkedAccountsKey : null,
    (path: string) => authorizedGet(path, accessToken as string),
  );
  const telegramAccount = useMemo(
    () =>
      data.linked_accounts.find((account) => account.platform === "telegram"),
    [data.linked_accounts],
  );
  const [modalState, setModalState] = useState<ModalStatus>({ status: "idle" });

  const popupRef = useRef<Window | null>(null);
  const popupCheckIntervalRef = useRef<number | null>(null);
  const authMessageReceivedRef = useRef(false);
  const gameName = game?.data?.name || "this game's";
  const questCompletionCalledRef = useRef(false);

  // Determine initial state based on quest type and linked account
  useEffect(() => {
    if (modalState.status !== "idle") {
      return;
    }

    if (quest.key === "telegram-v2" && gameName === IMMUTABLE_GAME_NAME) {
      setModalState({ status: "linkPassport" });
    } else if (quest.key === "telegram-v2") {
      setModalState({ status: "promptJoinChat", gameName });
    } else if (quest.key === "telegram-bot-connect") {
      setModalState({
        status: "promptClaimBotQuest",
        username: telegramAccount?.username,
      });
    }
  }, [telegramAccount, quest.key, gameName, modalState.status]);

  const cleanupTimers = useCallback(() => {
    if (popupCheckIntervalRef.current !== null) {
      window.clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanupTimers();
      popupRef.current?.close();
    };
  }, [cleanupTimers]);

  const trackEvent = useCallback(
    ({
      action,
      control,
      controlType,
      extras,
    }: {
      action: string;
      control: string;
      controlType: string;
      extras?: Record<string, unknown>;
    }) => {
      trackLegacyEvent(
        {
          screen: "TelegramQuestModal",
          userJourney: "Games",
          action,
          control,
          controlType,
          extras: {
            ruleId: quest.ruleId,
            gameName,
            modalStatus: modalState.status,
            ...(extras ? extras : {}),
          },
        },
        "Play",
      );
    },
    [quest.ruleId, gameName, modalState.status],
  );

  // Listener for messages from the callback popup
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== TELEGRAM_MESSAGE_EVENT_ORIGIN) {
        return;
      }

      authMessageReceivedRef.current = true;
      cleanupTimers();
      popupRef.current?.close();

      setModalState({ status: "loginProcessing" });

      try {
        const data = JSON.parse(event.data);
        const authResult = JSON.stringify(data.result);
        if (!authResult) {
          setModalState({
            status: "loginError",
            error: {
              message: "Telegram login failed",
              details: GENERIC_ERROR_MESSAGE_DETAIL,
            },
          });
          return;
        }

        if (!accessToken) {
          setModalState({
            status: "loginError",
            error: {
              message: "Play login required",
              details:
                "Cannot verify quest completion without being logged in.",
            },
          });
          return;
        }

        const { external_user_id } = await authorizedPost<{
          external_user_id: string;
        }>(
          "/passport-profile/v1/telegram-auth",
          {
            token: Buffer.from(authResult).toString("base64"),
          },
          accessToken,
        );

        mutateLinkedAccounts();
        trackEvent({
          action: "TelegramLoginCallback",
          controlType: "TelegramMessage",
          control: "ConnectAccount",
          extras: {
            telegram_user_id: external_user_id,
          },
        });
        setModalState({ status: "completingQuest" });
      } catch (error) {
        const apiError = error as ApiError;
        const apiErrorMessage = apiError?.response?.data?.message;

        if (
          apiErrorMessage?.includes(
            SOCIAL_LINK_ERROR_MESSAGES.SOCIAL_ACCOUNT_ALREADY_LINKED,
          )
        ) {
          setModalState({ status: "loginErrorSocialAccountAlreadyLinked" });
        } else {
          setModalState({
            status: "loginError",
            error: {
              message: "Telegram login failed",
              details: GENERIC_ERROR_MESSAGE_DETAIL,
            },
          });
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [accessToken, mutateLinkedAccounts, cleanupTimers, trackEvent]);

  const handleLogin = useCallback(() => {
    trackEvent({
      action: "Pressed",
      controlType: "Button",
      control:
        modalState.status !== "linkPassport"
          ? "Try Again"
          : "Connect to Telegram",
    });
    setModalState({ status: "loggingIn" });
    authMessageReceivedRef.current = false;
    cleanupTimers();

    const origin = window.location.origin;
    const url = `https://oauth.telegram.org/auth?bot_id=${appConfig.TELEGRAM_BOT_ID}&origin=${encodeURIComponent(origin)}&request_access=write`;

    const width = 450;
    const height = 600;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    const popup = window.open(
      url,
      TELEGRAM_POPUP_NAME,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`,
    );
    popupRef.current = popup;

    if (popup) {
      popup.focus();

      popupCheckIntervalRef.current = window.setInterval(() => {
        // Check if popup exists and is closed
        if (!popupRef.current || popupRef.current.closed) {
          cleanupTimers(); // Stop interval immediately

          // Give a grace period ONLY if we haven't received the message
          if (!authMessageReceivedRef.current) {
            setTimeout(() => {
              // Double-check flag after grace period
              if (!authMessageReceivedRef.current) {
                // Only set popupClosed state if we are still in loggingIn state
                // It might have transitioned to loginProcessing or loginError already
                setModalState((currentState) => {
                  if (currentState.status === "loggingIn") {
                    return { status: "popupClosed" };
                  }
                  return currentState;
                });
              }
            }, AUTH_GRACE_PERIOD_MS);
          }
        }
      }, POPUP_CHECK_INTERVAL_MS);
    } else {
      setModalState({
        status: "loginError",
        error: {
          message: "Failed to open popup",
          details: "Please allow popups for this site and try again.",
        },
      });
    }
  }, [cleanupTimers, trackEvent, modalState.status]);

  const handleTriggerCompleteQuest = useCallback(() => {
    setModalState({ status: "completingQuest" });
  }, []);

  const handleCompleteSocialQuest = useCallback(async () => {
    trackEvent({
      action: "Pressed",
      controlType: "Button",
      control:
        modalState.status === "promptJoinChat"
          ? "I've done this"
          : "Check again",
    });

    if (!accessToken) {
      setModalState({
        status: "completingQuestError",
        error: {
          message: "Play login required",
          details: "Cannot verify quest completion without being logged in.",
        },
      });
      return;
    }

    if (!telegramAccount) {
      setModalState({ status: "linkPassport" });
      return;
    }

    try {
      const res = await authorizedPost<{ following: boolean }>(
        "/passport-profile/v1/telegram-quest",
        {
          game_id: game?.data?.games_service_item?.id,
          quest_id: quest.ruleId,
        },
        accessToken,
      );

      if (res.following) {
        questCompletionCalledRef.current = false;
        onQuestComplete();
        onClose();
      } else {
        // User hasn't joined the chat yet
        questCompletionCalledRef.current = false;
        setModalState({ status: "notJoinedChat", gameName });
      }
    } catch (error) {
      questCompletionCalledRef.current = false;
      setModalState({
        status: "completingQuestError",
        error: {
          message: "Failed to verify chat membership",
          details: GENERIC_ERROR_MESSAGE_DETAIL,
        },
      });
    }
  }, [
    accessToken,
    telegramAccount,
    game?.data?.games_service_item?.id,
    quest.ruleId,
    onQuestComplete,
    onClose,
    gameName,
    trackEvent,
    modalState.status,
  ]);

  const handleCompleteLinkTelegramBotQuest = useCallback(async () => {
    try {
      trackEvent({
        action: "Pressed",
        controlType: "Button",
        control: "Claim Rewards",
      });
      questCompletionCalledRef.current = false;
      onQuestComplete();
      onClose();
    } catch (error) {
      questCompletionCalledRef.current = false;
      setModalState({
        status: "completingQuestError",
        error: {
          message: "Failed to complete quest",
          details: GENERIC_ERROR_MESSAGE_DETAIL,
        },
      });
    }
  }, [onQuestComplete, onClose, trackEvent]);

  useEffect(() => {
    if (
      modalState.status !== "completingQuest" ||
      isValidatingLinkedAccounts ||
      questCompletionCalledRef.current
    ) {
      return;
    }

    if (!telegramAccount) {
      setModalState({ status: "linkPassport" });
      return;
    }

    questCompletionCalledRef.current = true;

    if (quest.key === "telegram-v2" && gameName === IMMUTABLE_GAME_NAME) {
      onClose();
    } else if (quest.key === "telegram-v2") {
      handleCompleteSocialQuest();
    } else if (quest.key === "telegram-bot-connect") {
      handleCompleteLinkTelegramBotQuest();
    }
  }, [
    modalState.status,
    isValidatingLinkedAccounts,
    telegramAccount,
    quest.key,
    handleCompleteSocialQuest,
    handleCompleteLinkTelegramBotQuest,
    onClose,
    gameName,
  ]);

  const handleClose = useCallback(() => {
    trackEvent({
      action: "Pressed",
      controlType: "Button",
      control: "Close",
    });
    onClose();
  }, [onClose, trackEvent]);

  const contentProps = useMemo(() => {
    switch (modalState.status) {
      case "idle":
        return {
          title: "Loading...",
          caption: "Please wait...",
          button: null,
          action: null,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "linkPassport":
        return {
          title: "One more step: You'll need to connect your accounts",
          caption: `${gameName !== IMMUTABLE_GAME_NAME ? "Required to be able to claim the rewards." : ""} You'll also get game and promo updates.`,
          button: "Connect to Telegram",
          action: handleLogin,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: true,
        };
      case "loggingIn":
        return {
          title: "Connecting to Telegram...",
          caption: `${gameName !== IMMUTABLE_GAME_NAME ? "You'll need to do this to claim the rewards." : ""}Please complete the login process in the popup window.`,
          button: null,
          action: null,
          showClose: false,
          onClose: handleClose,
          showLinkAccountImages: true,
        };
      case "loginProcessing":
        return {
          title: "Connecting to Telegram...",
          caption: "Verifying your Telegram account...",
          button: null,
          action: null,
          showClose: false,
          onClose: handleClose,
          showLinkAccountImages: true,
        };
      case "loginError":
        return {
          title: modalState.error.message,
          caption: modalState.error.details || "Please try again.",
          button: "Try Again",
          action: handleLogin,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "loginErrorSocialAccountAlreadyLinked":
        return {
          title: "Telegram account already linked",
          caption: `${SOCIAL_LINK_ERROR_MESSAGES.SOCIAL_ACCOUNT_ALREADY_LINKED}. Please use a different Telegram account or contact support.`,
          button: "Close",
          action: onClose,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "popupClosed":
        return {
          title: "Telegram login window was closed",
          caption: `You closed the Telegram login window before completing the process. Please try again to complete ${
            gameName === IMMUTABLE_GAME_NAME ? "account linking" : "this quest"
          }.`,
          button: "Try Again",
          action: handleLogin,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "promptJoinChat":
        return {
          title: `Did you join ${modalState.gameName} on Telegram?`,
          button: "I've done this",
          action: handleTriggerCompleteQuest,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "completingQuest":
        return {
          title: "Almost there",
          caption: "Don't close this window.",
          button: null,
          action: null,
          showClose: false,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "notJoinedChat":
        return {
          title: `Please join the ${modalState.gameName} Telegram chat`,
          caption: `You haven't joined the ${modalState.gameName} Telegram chat yet. Please join the chat first, then click \"Check again\".`,
          button: "Check again",
          action: handleTriggerCompleteQuest,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "promptClaimBotQuest":
        return {
          title: "Get updates from the Immutable Telegram bot",
          caption: modalState.username
            ? `You've already linked your Telegram account @${modalState.username}.
            Click the button below to claim the rewards!`
            : undefined,
          button: "Claim Rewards",
          action: handleTriggerCompleteQuest,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      case "completingQuestError":
        return {
          title: modalState.error.message,
          caption: modalState.error.details || "Please try again later.",
          button: "Close",
          action: onClose,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
      default:
        notifyError(
          "Unexpected Telegram quest modal state",
          "telegramQuest",
          modalState,
        );
        return {
          title: "Unexpected error",
          caption: "Something went wrong. Please try again later.",
          button: "Close",
          action: onClose,
          showClose: true,
          onClose: handleClose,
          showLinkAccountImages: false,
        };
    }
  }, [
    modalState,
    handleLogin,
    handleTriggerCompleteQuest,
    onClose,
    gameName,
    handleClose,
  ]);

  return <TelegramQuestModalContent {...contentProps} />;
};
